import * as tf from '@tensorflow/tfjs';

export default class TensorFlowModelInitiator {
    static instance = null;
    static modelPath = "/assets/tensorflowModel/model.json";
    
    // États possibles
    static STATES = {
        IDLE: 'idle',
        LOADING: 'loading',
        READY: 'ready',
        ERROR: 'error'
    };

    constructor() {
        if (TensorFlowModelInitiator.instance) {
            return TensorFlowModelInitiator.instance;
        }

        this.model = null;
        this.state = TensorFlowModelInitiator.STATES.IDLE;
        this.loadPromise = null;
        this.callbacks = {
            onReady: [],
            onError: [],
            onStateChange: []
        };
        
        // Configuration TensorFlow.js pour optimiser les performances
        this.configureTensorFlow();
        
        TensorFlowModelInitiator.instance = this;
    }

    /**
     * Configuration optimisée de TensorFlow.js
     */
    configureTensorFlow() {
        // // Configuration pour économiser la mémoire
        // tf.env().set('WEBGL_PACK', false);
        // tf.env().set('WEBGL_FORCE_F16_TEXTURES', true);
        // tf.env().set('WEBGL_DELETE_TEXTURE_THRESHOLD', 0);
        
        // // Configuration pour les performances
        // tf.env().set('WEBGL_DELETE_TEXTURE_THRESHOLD', 0);
        // tf.env().set('WEBGL_PACK_BINARY_OPERATIONS', false);
    }

    /**
     * Singleton - obtenir l'instance unique
     */
    static getInstance() {
        if (!TensorFlowModelInitiator.instance) {
            TensorFlowModelInitiator.instance = new TensorFlowModelInitiator();
        }
        return TensorFlowModelInitiator.instance;
    }

    /**
     * Initialiser le modèle (chargement unique)
     */
    async initialize() {
        if (this.state === TensorFlowModelInitiator.STATES.READY) {
            return this.model;
        }

        if (this.state === TensorFlowModelInitiator.STATES.LOADING) {
            return this.loadPromise;
        }

        if (this.state === TensorFlowModelInitiator.STATES.ERROR) {
            throw new Error("Modèle en état d'erreur, redémarrage nécessaire");
        }

        this.setState(TensorFlowModelInitiator.STATES.LOADING);
        
        this.loadPromise = this.loadModel(TensorFlowModelInitiator.modelPath);
        
        try {
            this.model = await this.loadPromise;
            this.setState(TensorFlowModelInitiator.STATES.READY);
            this.notifyCallbacks('onReady', this.model);
            return this.model;
        } catch (error) {
            this.setState(TensorFlowModelInitiator.STATES.ERROR);
            this.notifyCallbacks('onError', error);
            throw error;
        } finally {
            this.loadPromise = null;
        }
    }

    /**
     * Charger le modèle TensorFlow.js
     */
    async loadModel(modelPath) {
        try {
            return await tf.loadGraphModel(modelPath);
        } catch (error) {
            console.error("Erreur lors du chargement du modèle TensorFlow.js:", error);
            throw error;
        }
    }

    /**
     * Vérifier si le modèle est prêt
     */
    isReady() {
        return this.state === TensorFlowModelInitiator.STATES.READY && this.model !== null;
    }

    /**
     * Vérifier si le modèle est en cours de chargement
     */
    isLoading() {
        return this.state === TensorFlowModelInitiator.STATES.LOADING;
    }

    /**
     * Vérifier si le modèle est en erreur
     */
    hasError() {
        return this.state === TensorFlowModelInitiator.STATES.ERROR;
    }

    /**
     * Obtenir l'état actuel
     */
    getState() {
        return this.state;
    }

    /**
     * Obtenir le modèle (null si pas chargé)
     */
    getModel() {
        return this.model;
    }

    /**
     * Attendre que le modèle soit prêt
     */
    async waitForReady() {
        if (this.isReady()) {
            return this.model;
        }

        if (this.isLoading()) {
            return this.loadPromise;
        }

        if (this.hasError()) {
            throw new Error("Modèle en état d'erreur");
        }

        // Si pas encore initialisé, l'initialiser
        return this.initialize();
    }

    /**
     * Ajouter un callback pour les événements
     */
    on(event, callback) {
        if (this.callbacks[event]) {
            this.callbacks[event].push(callback);
        }
    }

    /**
     * Supprimer un callback
     */
    off(event, callback) {
        if (this.callbacks[event]) {
            const index = this.callbacks[event].indexOf(callback);
            if (index > -1) {
                this.callbacks[event].splice(index, 1);
            }
        }
    }

    /**
     * Notifier les callbacks
     */
    notifyCallbacks(event, data) {
        if (this.callbacks[event]) {
            this.callbacks[event].forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    console.error(`Erreur dans le callback ${event}:`, error);
                }
            });
        }
    }

    /**
     * Changer l'état et notifier
     */
    setState(newState) {
        const oldState = this.state;
        this.state = newState;
        
        if (oldState !== newState) {
            this.notifyCallbacks('onStateChange', {
                oldState,
                newState,
                model: this.model
            });
        }
    }

    /**
     * Redémarrer le modèle (en cas d'erreur)
     */
    async restart() {
        await this.dispose();
        this.state = TensorFlowModelInitiator.STATES.IDLE;
        this.model = null;
        this.loadPromise = null;
        return this.initialize();
    }

    /**
     * Libérer les ressources
     */
    async dispose() {
        if (this.model) {
            try {
                this.model.dispose();
            } catch (error) {
                console.warn("Erreur lors de la libération du modèle:", error);
            }
            this.model = null;
        }
        this.setState(TensorFlowModelInitiator.STATES.IDLE);
    }

    /**
     * Méthode statique pour obtenir le modèle (convenience)
     */
    static async getModel() {
        const instance = TensorFlowModelInitiator.getInstance();
        return instance.waitForReady();
    }

    /**
     * Méthode statique pour vérifier si prêt (convenience)
     */
    static isModelReady() {
        const instance = TensorFlowModelInitiator.getInstance();
        return instance.isReady();
    }
}